import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { getProductListAsync } from "src/api/product/product-api";
import { CostType, ProductApprovalType, ProductListParams, ProductStatus, SalePeriodType } from "src/api/product/product-types";
import { BaseButton, BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { useQueryParams } from "src/hooks/useQueryParams";
import PagePath from "src/pagePath.json";
import { axiosInstance } from "src/utils";
import { ProductColumns } from "./columns";
import {
  approvalTypeOptions,
  costTypeOptions,
  isContractAvailOptions,
  isProductsUsedOptions,
  productStatusOptions,
  productTypeOptions,
  salePeriodTypeOptions,
  searchTypeOptions,
} from "./product-types";
import { Order } from "src/api/public-types";
import { useLocation } from "react-router-dom";
import SearchResetButton from "src/components/SearchResetButton";

/* 
  공간상품 > 목록 화면
 */

const ProductList = () => {
  const navigate = useNavigate();
  const { isAuthority } = usePartnerAuthority();
  const location = useLocation();

  // URL 쿼리 파라미터 관리
  const { queryParams, queryParamsNavigate } = useQueryParams<ProductListParams>({
    page: 0,
    size: 20,
    sort: { orders: [{ property: "id", direction: "DESC" }] },
  });

  // 검색어 입력 상태 관리
  const [searchParams, setSearchParams] = useState({
    keyword: queryParams.keyword || "",
    searchType: queryParams.searchType || "",
  });

  // URL 파라미터가 변경될 때 검색 상태 동기화
  useEffect(() => {
    setSearchParams({
      keyword: queryParams.keyword || "",
      searchType: queryParams.searchType || "",
    });
  }, [location.search, queryParams]);

  // 상품 목록 조회
  const { data: productListRes } = useQuery({
    queryKey: ["getProductListAsync", queryParams, location],
    queryFn: () => {
      // 검색 타입이 '상품 ID'면 keyword를 id 파라미터로 전달 (searchType/keyword는 미전송)
      const apiParams: ProductListParams = { ...queryParams };
      if (apiParams.searchType === "PRODUCT_ID") {
        apiParams.id = apiParams.keyword ?? "";
        delete apiParams.searchType;
        delete apiParams.keyword;
      } else {
        delete apiParams.id;
      }
      return getProductListAsync(axiosInstance, apiParams).then((res) => res.data);
    },
  });

  // 검색 필터 변경 시 URL 업데이트
  const updateSearchFilter = (updates: Partial<ProductListParams>) => {
    queryParamsNavigate({ ...queryParams, ...updates, page: 0 });
  };

  return (
    <div className="page-product-list">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <BaseInputWithSearch
                  type="text"
                  selectValue={searchParams.searchType}
                  inputValue={searchParams.keyword}
                  stateOptions={searchTypeOptions}
                  setStateValue={(searchType: string) => setSearchParams((prev) => ({ ...prev, searchType }))}
                  onChange={(keyword: string) => setSearchParams((prev) => ({ ...prev, keyword }))}
                  onClearClick={() => {
                    updateSearchFilter({ keyword: "", searchType: "" });
                  }}
                  onKeyUp={() => updateSearchFilter(searchParams)}
                  onSearchClick={() => updateSearchFilter(searchParams)}
                />
                <div className="minmax160">
                  <BaseSelect
                    value={queryParams.productType || ""}
                    stateOptions={productTypeOptions}
                    setStateValue={(value: string) => updateSearchFilter({ productType: value })}
                    placeholder="상품 유형"
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={queryParams.approvalType || ""}
                    stateOptions={approvalTypeOptions}
                    setStateValue={(value: ProductApprovalType) => updateSearchFilter({ approvalType: value })}
                    placeholder="승인 유형"
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={queryParams.isUsed || ""}
                    stateOptions={isProductsUsedOptions}
                    setStateValue={(value: string) => updateSearchFilter({ isUsed: value })}
                    placeholder="이용여부"
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={queryParams.isContractAvail || ""}
                    stateOptions={isContractAvailOptions}
                    setStateValue={(value: string) => updateSearchFilter({ isContractAvail: value })}
                    placeholder="계약 가능 여부"
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={queryParams.status || ""}
                    stateOptions={productStatusOptions}
                    setStateValue={(value: ProductStatus) => updateSearchFilter({ status: value })}
                    placeholder="공개 유형"
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={queryParams.salePeriodType || ""}
                    stateOptions={salePeriodTypeOptions}
                    setStateValue={(value: SalePeriodType) => updateSearchFilter({ salePeriodType: value })}
                    placeholder="판매 방식"
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={queryParams.costType || ""}
                    stateOptions={costTypeOptions}
                    setStateValue={(value: CostType) => updateSearchFilter({ costType: value })}
                    placeholder="납부 유형"
                  />
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          {isAuthority("w") && (
            <div className="right-area">
              <BaseButton title="+ 상품 등록" onClick={() => navigate(PagePath.product.form)} />
            </div>
          )}
        </div>
        {productListRes?.data?.content && (
          <BaseTable
            data={productListRes.data.content}
            columns={ProductColumns}
            currentSize={Number(queryParams.size) || 20}
            sizeOption={(size) => updateSearchFilter({ size })}
            pageIndex={Number(queryParams?.page || 0)}
            totalPages={productListRes.meta.pageMeta?.totalPages || 0}
            totalElements={productListRes.meta.pageMeta?.totalElements}
            goPage={(page: number) => queryParamsNavigate({ ...queryParams, page })}
            orders={queryParams.sort?.orders}
            setOrders={(orders: Order[]) => orders && updateSearchFilter({ sort: { orders } })}
            disabledSortHeaders={["rentalCost", "approvalType", "costType"]}
          />
        )}
      </div>
    </div>
  );
};

export default ProductList;
